/**
 * Editable preview table and submit action for bulk administrator creation.
 */

import { observer } from 'mobx-react-lite';

import Card from '../../../../../components/layouts/card/card';
import { Button } from '../../../../../components/inputs/button/button';
import { ButtonIcon } from '../../../../../components/inputs/button-icon/button-icon';
import { Input } from '../../../../../components/inputs/input/input';
import { Checkbox, CHECKBOX_SIZES } from '../../../../../components/inputs/checkbox/checkbox';
import { BUTTON_VARIANTS } from '../../../../../components/inputs/button-variants';
import FontHeading4 from '../../../../../components/fonts/font-heading4/font-heading4';
import Table from '../../../../../components/table/table';
import TableRow from '../../../../../components/table/components/table-row/table-row';
import IconDelete from '../../../../../components/icons/icon-delete/icon-delete';
import { ICreateAdministratorUser } from '../../../../../services/api/troubleshooting-service';
import createAdministratorsStore from '../stores/create-administrators-store';

const PREVIEW_COLUMNS = [
    { content: 'Email', width: { value: 1.5, type: 'fr' as const } },
    { content: 'First Name', width: { value: 1, type: 'fr' as const } },
    { content: 'Last Name', width: { value: 1, type: 'fr' as const } },
    { content: 'Display Name', width: { value: 1, type: 'fr' as const } },
    { content: 'Viewer', width: { value: 0.5, type: 'fr' as const } },
    { content: 'Editor', width: { value: 0.5, type: 'fr' as const } },
    { content: '', width: { value: 40, type: 'px' as const } },
];

const CreateAdministratorsPreviewCard: React.FC = observer(() => {
    const store = createAdministratorsStore;

    if (store.users.length === 0) {
        return null;
    }

    return (
        <Card isExpandable={false}>
            <FontHeading4>Preview ({store.users.length} users)</FontHeading4>
            <div className="create-administrators-page__table-wrapper">
                <Table columns={PREVIEW_COLUMNS}>
                    {store.users.map((administratorRow: ICreateAdministratorUser, rowIndex: number) => (
                        <TableRow
                            key={`${administratorRow.email}-${rowIndex}`}
                            cells={[
                                <Input
                                    value={administratorRow.email}
                                    onChange={(value) => store.updateUser(rowIndex, 'email', value)}
                                />,
                                <Input
                                    value={administratorRow.first_name}
                                    onChange={(value) => store.updateUser(rowIndex, 'first_name', value)}
                                />,
                                <Input
                                    value={administratorRow.last_name}
                                    onChange={(value) => store.updateUser(rowIndex, 'last_name', value)}
                                />,
                                <Input
                                    value={administratorRow.display_name}
                                    onChange={(value) => store.updateUser(rowIndex, 'display_name', value)}
                                />,
                                <Checkbox
                                    label=""
                                    size={CHECKBOX_SIZES.SMALL}
                                    checked={administratorRow.viewer ?? true}
                                    onChange={(checked) => store.updateUser(rowIndex, 'viewer', checked)}
                                />,
                                <Checkbox
                                    label=""
                                    size={CHECKBOX_SIZES.SMALL}
                                    checked={administratorRow.editor ?? true}
                                    onChange={(checked) => store.updateUser(rowIndex, 'editor', checked)}
                                />,
                                <ButtonIcon
                                    Component={IconDelete}
                                    onClick={() => store.removeUser(rowIndex)}
                                    color="var(--color-accent-3)"
                                />,
                            ]}
                            skipWrapingCell
                        />
                    ))}
                </Table>
            </div>
            <div className="troubleshooting-buttons-container">
                <Button
                    text="Create administrators"
                    variant={BUTTON_VARIANTS.Primary}
                    onClick={() => store.submit()}
                    isLoading={store.isSubmitting}
                    hasLoader
                    disabled={store.isSubmitting}
                />
            </div>
        </Card>
    );
});

export default CreateAdministratorsPreviewCard;
